import { Node } from "@tiptap/core";
import { quoteFieldLabel } from "./quoteFields";
import { FIELD_CHIP_NODE_NAME } from "./textEditModel";

/**
 * Edit-session pill styling for field chips. Literal class string on
 * purpose: the Tailwind build-time scanner picks the classes up here.
 *
 * The pill is deliberately selectable (no `select-none`): Chromium stops a
 * drag selection dead at a `user-select: none` island inside an editable and
 * paints no highlight over it, which made a chip impossible to sweep over with
 * the mouse. A click directly on the chip node-selects it, shown by the same
 * highlight tint the surrounding text selection paints.
 */
const CHIP_EDITING_CLASSES =
  "rounded bg-blue-100 px-1 text-blue-900 cursor-default whitespace-nowrap [&.ProseMirror-selectednode]:bg-blue-500/30";

/**
 * The chip's bound source, written into the pill it renders. Copying a chip
 * serializes it through that pill, so without the attribute the clipboard
 * carries only the label and the paste degrades to plain text.
 */
const CHIP_SOURCE_ATTRIBUTE = "data-fp-chip-source";

/**
 * Atomic inline TipTap node for an `<fp-field>` chip: renders the resolved
 * field value (or a visible placeholder) as a non-editable pill. Its `source`
 * round-trips back to a `{{ source }}` markdown token on commit through
 * textEditModel.ts.
 */
export const FieldChipNode = Node.create({
  name: FIELD_CHIP_NODE_NAME,
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      source: {
        default: "",
        parseHTML: (element) => element.getAttribute(CHIP_SOURCE_ATTRIBUTE),
        renderHTML: (attributes) => ({
          [CHIP_SOURCE_ATTRIBUTE]: attributes.source,
        }),
      },
      label: {
        default: "",
        parseHTML: (element) => element.textContent,
        rendered: false,
      },
    };
  },

  parseHTML() {
    return [{ tag: `span[${CHIP_SOURCE_ATTRIBUTE}]` }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const label = typeof node.attrs.label === "string" ? node.attrs.label : "";
    const source =
      typeof node.attrs.source === "string" ? node.attrs.source : "";
    const title = quoteFieldLabel(source !== "" ? source : null);
    return [
      "span",
      {
        ...HTMLAttributes,
        class: CHIP_EDITING_CLASSES,
        ...(title !== null ? { "data-field-title": title } : {}),
      },
      label,
    ];
  },
});
